import { useState, useEffect } from 'react';
import axios from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  ArrowPathIcon,
  BellAlertIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import FinanceLockGate from '../components/FinanceLockGate';
import { MessageCircle } from 'lucide-react';

const RUPEE_SYMBOL = '\u20B9';

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDateValue(value, dateFormat = 'dd MMM yyyy') {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return format(parsed, dateFormat);
}

function formatCurrency(value) {
  return `${RUPEE_SYMBOL}${Number(value || 0).toFixed(2)}`;
}

function getEmiPatientName(item) {
  return item?.Invoice?.Patient?.name || 'Patient';
}

function getStageLabel(stage) {
  if (stage === 'before_due') return 'T-1 / T-2';
  if (stage === 'soft_followup') return 'Soft follow-up';
  if (stage === 'aggressive_followup') return 'Aggressive follow-up';
  return 'Reminder';
}

function getStageClasses(stage) {
  if (stage === 'aggressive_followup') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }
  if (stage === 'soft_followup') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  return 'border-indigo-200 bg-indigo-50 text-indigo-700';
}

function getOverdueStage(item) {
  if (item?.reminderStage) return item.reminderStage;
  return Number(item?.daysFromToday || 0) < -7 ? 'aggressive_followup' : 'soft_followup';
}

function isSameLocalDate(left, right) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function hasSentToday(item, localSentAt) {
  const sentValue = localSentAt || item?.reminderSentAt;
  if (!sentValue) return false;

  const sentAt = new Date(sentValue);
  if (Number.isNaN(sentAt.getTime())) return false;

  return isSameLocalDate(sentAt, new Date());
}

function getDaysFromToday(item) {
  if (Number.isFinite(Number(item?.daysFromToday))) {
    return Number(item.daysFromToday);
  }

  if (!item?.dueDate) return 0;
  const dueDate = new Date(item.dueDate);
  if (Number.isNaN(dueDate.getTime())) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  return Math.round((dueDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

function sortMissedByUrgency(rows) {
  return [...rows].sort((left, right) => {
    const dayDiff = getDaysFromToday(left) - getDaysFromToday(right);
    if (dayDiff !== 0) return dayDiff;
    return new Date(left.dueDate || 0).getTime() - new Date(right.dueDate || 0).getTime();
  });
}

function sortDueSoonByUrgency(rows) {
  return [...rows].sort((left, right) => {
    const dayDiff = getDaysFromToday(left) - getDaysFromToday(right);
    if (dayDiff !== 0) return dayDiff;
    return new Date(left.dueDate || 0).getTime() - new Date(right.dueDate || 0).getTime();
  });
}

export default function AutoReminders() {
  const [dueEmis, setDueEmis] = useState([]);
  const [overdueEmis, setOverdueEmis] = useState([]);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [sendingKey, setSendingKey] = useState('');
  const [statusKey, setStatusKey] = useState('');
  const [financeReady, setFinanceReady] = useState(false);
  const [sentReminders, setSentReminders] = useState({});

  useEffect(() => {
    if (financeReady) {
      fetchFinanceReminders();
    }
  }, [financeReady]);

  const fetchFinanceReminders = async () => {
    setFinanceLoading(true);
    try {
      const emiRes = await axios.get('/patients/emi-alerts');
      const emiData = emiRes.data || {};

      setDueEmis(sortDueSoonByUrgency(safeArray(emiData.dueSoon || emiData.dueToday)));
      setOverdueEmis(sortMissedByUrgency(safeArray(emiData.overdue)));
    } catch (err) {
      console.error('Failed to load EMI reminders:', err);
      setDueEmis([]);
      setOverdueEmis([]);
      toast.error(err.response?.data?.error || 'Failed to load EMI reminders');
    } finally {
      setFinanceLoading(false);
    }
  };

  const fetchReminders = async () => {
    if (financeReady) {
      await fetchFinanceReminders();
    }
  };

  const markReminderSent = (type, id, sentAt = new Date().toISOString()) => {
    setSentReminders((current) => ({
      ...current,
      [`${type}-${id}`]: sentAt,
    }));
  };

  const patchEmiReminderSentAt = (id, reminderSentAt) => {
    const patchList = (rows) =>
      rows.map((row) => (row.id === id ? { ...row, reminderSentAt: reminderSentAt || row.reminderSentAt } : row));

    setDueEmis(patchList);
    setOverdueEmis(patchList);
  };

  const sendReminder = async (item) => {
    const id = item.id;
    const status = item.paymentStatus || item.status;

    if (status === 'Done' || status === 'Paid') {
      toast.error('Payment already done. Reminder not prepared.');
      return;
    }

    const key = `emi-${id}`;
    setSendingKey(key);

    try {
      // Future auto-send provider hook: replace only this endpoint/open-wa.me block with provider delivery.
      const res = await axios.post(`/patients/emi/${id}/remind`);

      const whatsappUrl = res?.data?.whatsappUrl;
      if (whatsappUrl) {
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      }

      const sentAt = res?.data?.reminderSentAt || new Date().toISOString();
      markReminderSent('emi', id, sentAt);
      patchEmiReminderSentAt(id, sentAt);
      toast.success('WhatsApp message prepared');
    } catch (err) {
      console.error('Failed to prepare reminder:', err);
      toast.error(err.response?.data?.error || 'Failed to prepare reminder');
    } finally {
      setSendingKey('');
    }
  };

  const markEmiPaid = async (item) => {
    const key = `status-${item.id}`;
    setStatusKey(key);

    try {
      await axios.put(`/patients/emi/${item.id}/status`, {
        paymentStatus: 'Done',
      });

      toast.success('Payment marked paid');
      await fetchFinanceReminders();
    } catch (err) {
      console.error('Failed to update payment status:', err);
      toast.error(err.response?.data?.error || 'Failed to update payment status');
    } finally {
      setStatusKey('');
    }
  };

  const totalRemindersDue = dueEmis.length + overdueEmis.length;

  const SentTodayMarker = ({ item, type }) => {
    const localSentAt = sentReminders[`${type}-${item.id}`];
    if (!hasSentToday(item, localSentAt)) return null;

    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
        <CheckCircleIcon className="h-3.5 w-3.5" />
        Sent today
      </span>
    );
  };

  const ReminderSection = ({ title, description, count, icon, tone, loading, emptyText, children }) => (
    <section className="section-card overflow-hidden border border-slate-200/70 shadow-[0_8px_22px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tone}`}>
            {icon}
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>
          </div>
        </div>
        <span className="inline-flex self-start rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600">
          {count} due
        </span>
      </div>

      <div className="p-4">
        {loading ? (
          <p className="rounded-[18px] border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm font-medium text-slate-500">
            Loading reminders...
          </p>
        ) : count === 0 ? (
          <p className="rounded-[18px] border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm font-medium text-slate-500">
            {emptyText}
          </p>
        ) : (
          <ul className="space-y-3">{children}</ul>
        )}
      </div>
    </section>
  );

  const EmiReminderRow = ({ item, stage }) => {
    const reminderStage = stage || item.reminderStage || 'before_due';
    const actionKey = `emi-${item.id}`;
    const statusActionKey = `status-${item.id}`;

    return (
      <li className="flex flex-col gap-3 rounded-[18px] border border-slate-200 bg-slate-50/80 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-slate-900">{getEmiPatientName(item)}</p>
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${getStageClasses(reminderStage)}`}>
              {getStageLabel(reminderStage)}
            </span>
            <SentTodayMarker item={item} type="emi" />
          </div>
          <p className="mt-2 text-sm text-slate-600">
            {formatCurrency(item.amount)} due {formatDateValue(item.dueDate)}
          </p>
          <p className="mt-1 text-xs font-medium text-slate-400">
            {item.Invoice?.invoiceNumber ? `Invoice ${item.Invoice.invoiceNumber}` : 'EMI reminder'}
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={() => sendReminder(item)}
            disabled={sendingKey === actionKey}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
          >
            {sendingKey === actionKey ? 'Preparing...' : (
              <>
                <MessageCircle className="h-4 w-4" />
                Send on WhatsApp
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => markEmiPaid(item)}
            disabled={statusKey === statusActionKey}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-emerald-200 hover:text-emerald-700 disabled:opacity-60"
          >
            {statusKey === statusActionKey ? 'Updating...' : 'Mark paid'}
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600">
            Manual WhatsApp payment queue
          </p>
          <h1 className="page-title mt-1">Reminders Due</h1>
          <p className="page-subtitle">
            Dektal lists EMI payments that need follow-up. Staff tap once to open the prepared WhatsApp message with payment details and send it manually.
          </p>
        </div>

        <button
          type="button"
          onClick={fetchReminders}
          disabled={financeLoading}
          className="btn-primary self-start lg:self-auto"
        >
          <ArrowPathIcon className={`h-5 w-5 ${financeLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="section-card border border-blue-100 bg-blue-50/70 p-5 shadow-[0_8px_24px_rgba(37,99,235,0.05)]">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-3xl font-semibold tracking-tight text-slate-900">{totalRemindersDue}</p>
            <p className="mt-1 text-sm font-medium text-slate-600">payment reminders due</p>
          </div>
          <p className="max-w-2xl text-sm leading-6 text-slate-600">
            These are prepared messages only. Nothing is auto-sent from this screen; fully automatic delivery can be connected later through a WhatsApp provider.
          </p>
        </div>
      </div>

      <FinanceLockGate
        title="Unlock finance to view payment reminders"
        description="Payment reminders include EMI amounts, due dates, UPI details, and collection follow-up actions."
        onAccessGranted={() => setFinanceReady(true)}
      >
        <div className="space-y-5">
          <ReminderSection
            title="Payment missed"
            description="Overdue unpaid EMIs appear first, sorted by the most overdue payment at the top."
            count={overdueEmis.length}
            icon={<ExclamationTriangleIcon className="h-6 w-6" />}
            tone="bg-rose-100 text-rose-600"
            loading={financeLoading}
            emptyText="No missed payment reminders."
          >
            {overdueEmis.map((item) => (
              <EmiReminderRow key={item.id} item={item} stage={getOverdueStage(item)} />
            ))}
          </ReminderSection>

          <ReminderSection
            title="Payment due soon (T-1 / T-2)"
            description="Upcoming EMIs are sorted so T-1 appears above T-2."
            count={dueEmis.length}
            icon={<BellAlertIcon className="h-6 w-6" />}
            tone="bg-indigo-100 text-indigo-600"
            loading={financeLoading}
            emptyText="No payment reminders due soon."
          >
            {dueEmis.map((item) => (
              <EmiReminderRow key={item.id} item={item} stage="before_due" />
            ))}
          </ReminderSection>
        </div>
      </FinanceLockGate>
    </div>
  );
}
